import { useMemo } from 'react'
import { useLocation, useParams } from 'react-router'

/**
 * The pathname portion the nearest route actually matched, excluding any splat
 * (`*`) remainder — React Router v5's `match.url`.
 *
 * Lets a component rendered under a splat route (e.g. `/patients/:id/*`) build
 * absolute links to its own tabs or siblings without hardcoding the parent
 * path, and without relative `to` values, whose resolution inside splat routes
 * changes between router versions.
 */
export const useRouteBasePath = (): string => {
  const { pathname } = useLocation()
  const splat = useParams()['*']

  return useMemo(() => {
    const trimmedPathname = pathname.replace(/\/+$/, '')
    const trimmedSplat = splat?.replace(/\/+$/, '') ?? ''
    if (trimmedSplat && trimmedPathname.endsWith(`/${trimmedSplat}`)) {
      return trimmedPathname.slice(0, trimmedPathname.length - trimmedSplat.length - 1)
    }
    return trimmedPathname
  }, [pathname, splat])
}

/**
 * The path of a sibling tab, for a component rendered *as* a tab — one whose own
 * route base therefore ends in its tab segment. Swaps that segment for `tab`.
 *
 * Only correct at that depth. A component rendered beside the tabs rather than
 * inside the nav's nested `Routes` — the chat panel — already matches the chart
 * route itself, so it builds the same link as `${useRouteBasePath()}/${tab}`.
 */
export const useSiblingTabPath = (tab: string): string => {
  const basePath = useRouteBasePath()
  return `${basePath.slice(0, basePath.lastIndexOf('/'))}/${tab}`
}
